import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { IDashboardProgressState } from '@shared/models';
import * as Response from '@shared/models/dashboard/dashboard.response.type';
import { PILLAR_SKILLS_SKELETON_DATA } from 'apps/platform/src/lib/constants/skeleton-data/pillarSkills';

import { fetchSkillsProgress, fetchTrackProgress, fetchSpentTime, fetchRankComparison } from '../queries';

const state: IDashboardProgressState = {
    skillProgress: {
        statsPerCategories: PILLAR_SKILLS_SKELETON_DATA,
        averageCompletion: 0,
        averageGrade: 0,
        timeInMinutes: 0,
        totalModules: 0,
        completedModules: 0,
    },
    fetchingSkillsProgress: false,
    trackProgress: {
        prerequisiteSkills: null,
        internshipTrack: null,
        entrepreneurshipTrack: null,
    },
    fetchingTrackProgress: false,
    spentTime: {
        hoursLoggedLastWeek: 0,
        hoursLoggedThisWeek: 0,
        percentageChange: 0,
        percentageOfHoursLogged: 0,
    },
    fetchingSpentTime: false,
    rankComparison: {
        globalRank: 0,
        totalStudents: 0,
        topPercentage: 0,
        message: '',
    },
    fetchingRankComparison: false,
};

const dashboardSlice = createSlice({
    name: 'dashboard-progress',
    initialState: state,
    reducers: {
        setSkillProgress: (state, { payload }: PayloadAction<Response.ISkillsProgressResponseType>) => {
            state.skillProgress = payload;
        },
        setTrackProgress: (state, { payload }: PayloadAction<Response.ITracksProgressResponseType>) => {
            state.trackProgress = {
                prerequisiteSkills: payload.prerequisiteSkills,
                internshipTrack: payload.internshipTrack,
                entrepreneurshipTrack: payload.entrepreneurshipTrack,
            };
        },
        setSpentTime: (state, { payload }: PayloadAction<Response.ISpentTimeResponseType>) => {
            state.spentTime = {
                hoursLoggedLastWeek: payload?.hoursLoggedLastWeek || 0,
                hoursLoggedThisWeek: payload?.hoursLoggedThisWeek || 0,
                percentageChange: payload?.percentageChange || 0,
                percentageOfHoursLogged: payload?.percentageOfHoursLogged || 0,
            };
        },
        setRankComparison: (state, { payload }: PayloadAction<Response.IRankComparisonResponseType>) => {
            state.rankComparison = payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchSkillsProgress.pending, (state) => {
            state.fetchingSkillsProgress = true;
        });
        builder.addCase(fetchSkillsProgress.rejected, (state) => {
            state.fetchingSkillsProgress = false;
        });
        builder.addCase(fetchSkillsProgress.fulfilled, (state) => {
            state.fetchingSkillsProgress = false;
        });
        builder.addCase(fetchTrackProgress.pending, (state) => {
            state.fetchingTrackProgress = true;
        });
        builder.addCase(fetchTrackProgress.rejected, (state) => {
            state.fetchingTrackProgress = false;
        });
        builder.addCase(fetchTrackProgress.fulfilled, (state) => {
            state.fetchingTrackProgress = false;
        });
        builder.addCase(fetchSpentTime.pending, (state) => {
            state.fetchingSpentTime = true;
        });
        builder.addCase(fetchSpentTime.rejected, (state) => {
            state.fetchingSpentTime = false;
        });
        builder.addCase(fetchSpentTime.fulfilled, (state) => {
            state.fetchingSpentTime = false;
        });
        builder.addCase(fetchRankComparison.pending, (state) => {
            state.fetchingRankComparison = true;
        });
        builder.addCase(fetchRankComparison.rejected, (state) => {
            state.fetchingRankComparison = false;
        });
        builder.addCase(fetchRankComparison.fulfilled, (state) => {
            state.fetchingRankComparison = false;
        });
    },
});

export const { setSkillProgress, setTrackProgress, setSpentTime, setRankComparison } = dashboardSlice.actions;
export default dashboardSlice.reducer;
